import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Play,
  Pause,
  RotateCcw,
  Video,
  Subtitles,
  X as XIcon,
  Sparkles,
  Music4,
  Waves,
  ShoppingCart,
  ListMusic,
  Star,
  Share2,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Camera } from 'lucide-react';
import BrandIcon from '@/components/BrandIcon';

/**
 * Promo (/promo)
 * ---------------------------------------------------------------------------
 * A fully self-contained animated commercial that plays through the GroovLens
 * narration script in 9 scenes over ~60 seconds. Designed to be
 * SCREEN-RECORDED (on phone or desktop) as the raw video track; the user will
 * dub their own AI voiceover + music over the top in CapCut.
 *
 * Why build this instead of paying for a text-to-video AI:
 *   - Uses REAL GroovLens brand tokens (colors, typography, film grain) so it
 *     doesn't drift off-brand the way generative tools do.
 *   - Renders honest UI-shaped visuals (crate grids, scanner overlays, track
 *     cards) instead of AI-hallucinated fake interfaces.
 *   - Free, editable, and reproducible — bump the script or timings any time.
 *
 * Screen-record instructions live inline in the UI. `?record=1` in the URL
 * hides ALL chrome and autoplays from t=0 so the recording is edge-to-edge.
 *
 * Aspect ratio: locked to 9:16 (1080 x 1920 export), centered with black
 * letterboxing on desktop. Fullscreen native on phone.
 * ---------------------------------------------------------------------------
 */

// -----------------------------------------------------------------------------
// Scene timing table — every number below is authoritative.
//
// Add/remove/adjust here and everything (progress bar, subtitle lookup, scene
// switch) follows automatically. `startSec` values must be monotonically
// increasing; final `endSec` is the total runtime.
//
// Ranges expanded in the second cut so we could cover more features
// (music videos, ringtones, artist deep dives, AI similar tracks, custom
// AI covers, personal stats, PWA install) without any single scene feeling
// rushed.
// -----------------------------------------------------------------------------
const SCENES = [
  { key: 'cover',     startSec: 0,   endSec: 5,   line: '' },
  { key: 'folder',    startSec: 5,   endSec: 11,  line: 'Everyone has that folder \u2014 the one full of music screenshots you meant to look up later.' },
  { key: 'buried',    startSec: 11,  endSec: 19,  line: 'Two thousand screenshots. Tracks you meant to buy, share, remember. Buried in your camera roll, eating your phone\u2019s memory.' },
  { key: 'snapdrop',  startSec: 19,  endSec: 26,  line: 'GroovLens fixes it. Snap one, or drop two hundred at once. Sorted. Saved. Safe. Take your space back.' },
  { key: 'airead',    startSec: 26,  endSec: 33,  line: 'AI reads every pixel. Artist, title, album art, BPM, key, genre. No typing. No tagging.' },
  { key: 'crates',    startSec: 33,  endSec: 40,  line: 'Every track lands in the right crate. Thirty-three genres, each with an AI-painted cover \u2014 or design your own.' },
  { key: 'toolkit',   startSec: 40,  endSec: 49,  line: 'Previews. Buy on Beatport, Traxsource, Bandcamp, iTunes, Amazon. Music videos, ringtones. Parse whole tracklists at once.' },
  { key: 'discover',  startSec: 49,  endSec: 56,  line: 'Discover similar tracks by artist, vibe, or BPM. Dive deep into any artist\u2019s bio, top tracks, and related sound.' },
  { key: 'organize',  startSec: 56,  endSec: 64,  line: 'Flag what you own. Star what you want. Watch your stats grow. Share crates privately, or push to Spotify in one tap.' },
  { key: 'neverlost', startSec: 64,  endSec: 70,  line: 'That song you saved and forgot about? Still there. Still findable. Never lost. Never forgotten.' },
  { key: 'install',   startSec: 70,  endSec: 75,  line: 'Install GroovLens right from the site. No app store needed.' },
  { key: 'cta',       startSec: 75,  endSec: 82,  line: '' },
];
const TOTAL_SEC = SCENES[SCENES.length - 1].endSec;

// -----------------------------------------------------------------------------
// Genre bucket list used in the CratesScene. Mirrors the actual buckets your
// app supports; visual palettes are decorative but on-brand.
// -----------------------------------------------------------------------------
const GENRE_TILES = [
  { name: 'House',     hue: 175, sat: 78 },
  { name: 'Techno',    hue: 220, sat: 40 },
  { name: 'Hip-Hop',   hue: 30,  sat: 85 },
  { name: 'R&B',       hue: 340, sat: 60 },
  { name: 'Jazz',      hue: 45,  sat: 70 },
  { name: 'Rock',      hue: 12,  sat: 65 },
  { name: 'DnB',       hue: 265, sat: 55 },
  { name: 'Soul',      hue: 20,  sat: 80 },
  { name: 'Ambient',   hue: 190, sat: 45 },
];

// -----------------------------------------------------------------------------
// Demo track — a single source of truth for the "step-by-step demo" feel.
// Scene 4 (AI reads a screenshot) and Scene 7 (artist deep dive) pull from
// the same object so the video reads like one continuous flow: user drops a
// Marco Lys Instagram capture, sees the identified track card, then dives
// into Marco Lys's real artist page.
//
// Both images are real production screenshots saved to /public/promo/.
// -----------------------------------------------------------------------------
const DEMO_TRACK = {
  artist: 'Marco Lys',                                // primary artist for deep dive
  artistsFull: 'Marco Lys, Lovely Laura & Ben Santiago', // full credited line
  title: 'Real Love',
  album: 'Real Love \u2014 Single',
  label: 'Nervous Records',
  bpm: 124,
  key: 'A minor',
  genre: 'Tech House',
  screenshotSource: 'Instagram post',
  // Real screenshot of the identified track card (with peek-a-boo deck).
  trackCardSrc: '/promo/track-card.jpg',
  // Real screenshot of the Marco Lys artist deep dive page.
  artistDeepDiveSrc: '/promo/artist-deep-dive.jpg',
  // Real screenshot of the Discover Similar modal (with animated neon border).
  discoverSimilarSrc: '/promo/discover-similar.jpg',
  // Real Instagram screenshot (Nervous Records — Marco Lys "Real Love"). Used
  // as the "raw input" snap that threads continuity through Scenes 2, 3, and
  // 9 — buried in the camera roll → uploaded → scanned → still findable.
  rawSnapSrc: '/promo/snap-real-love.png',
  // Kept for legacy prop compatibility; not shown when trackCardSrc exists.
  screenshotSrc: null,
  albumArtSrc: null,
  albumArtGradient:
    'linear-gradient(135deg, hsl(50 80% 55%), hsl(30 60% 25%))',
  artistBio:
    "Italy's tech-house heavyweight. Born and raised in Venice, forging a unique tech-tinged house sound.",
  artistTags: ['tech house', 'house', 'italian', 'minimal', 'electro'],
  topTrackCount: 18,
  similar: [
    { title: 'Ecstasy',        artist: 'Italobros',     bpm: 125, hue: 285 },
    { title: 'Body Movin\u2019', artist: 'Jay de Lys',    bpm: 124, hue: 30  },
    { title: 'Late Night',     artist: 'James Hurr',    bpm: 126, hue: 210 },
  ],
};

// -----------------------------------------------------------------------------
// Reusable film-grain overlay. Uses a fixed SVG turbulence pattern so the
// noise is stable across frames (a randomized/animated grain would compress
// horribly on TikTok/Reels).
// -----------------------------------------------------------------------------
function FilmGrain({ intensity = 0.06 }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 mix-blend-overlay"
      style={{
        opacity: intensity,
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.7  0 0 0 0 0.7  0 0 0 0 0.7  0 0 0 1 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// Vignette + soft radial glow — used on every scene to unify the look.
// -----------------------------------------------------------------------------
function SceneBackdrop({ children }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0B0F14]">
      {/* Ambient teal glow behind subject */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, hsl(var(--primary) / 0.22) 0%, transparent 55%)',
        }}
      />
      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)',
        }}
      />
      {children}
      <FilmGrain />
    </div>
  );
}

// -----------------------------------------------------------------------------
// SCENE 0 (COVER) — Title card.
// A clean, quiet 3-second intro: giant vinyl logo mark, GroovLens wordmark,
// tagline underneath. Sets brand identity BEFORE the story starts. Deliberately
// silent (no VO caption) so it reads as a title card, not a talking scene.
// -----------------------------------------------------------------------------
function CoverScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="flex flex-col items-center text-center">
          {/* Big brand mark — pops in with a soft scale + fade. Uses the
              exact same BrandIcon component the app header, landing hero,
              auth pages, and PWA install tile use. One logo everywhere. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mb-5"
          >
            <BrandIcon
              size={140}
              variant="teal-disc"
              showTile={false}
              className="drop-shadow-[0_20px_50px_rgba(0,0,0,0.75)]"
            />
          </motion.div>
          {/* Wordmark — slides up slightly after the icon lands. */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.55, ease: 'easeOut' }}
            className="font-display text-5xl sm:text-6xl font-semibold tracking-tight text-white leading-none"
          >
            GroovLens
          </motion.div>
          {/* "by Beatseek" sub-attribution — small, italic, muted, right-aligned
              under the wordmark to match the BrandWordmark lockup used
              elsewhere in the app. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="text-xs italic text-white/50 mt-0.5 tracking-wide"
          >
            by Beatseek
          </motion.div>
          {/* Tagline — the brand's primary hero line. Fades in last, split
              across two lines for stronger cinematic pacing on the cover.
              Explicit text-center + mx-auto to guarantee both lines center
              inside the max-w constraint regardless of parent inheritance. */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.55, ease: 'easeOut' }}
            className="mt-6 text-base sm:text-lg font-mono text-[hsl(var(--primary))]/90 max-w-[320px] mx-auto text-center leading-snug"
            data-testid="promo-cover-tagline"
          >
            Turn snapshots into
            <br />
            a crate worth diggin&rsquo;
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 1 — "Everyone has that folder."
// A phone silhouette with a torrent of colored screenshot chips scrolling
// past. Blur/motion sim'd via translateY loop and blur filter.
// -----------------------------------------------------------------------------
function FolderScene() {
  const chips = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        id: i,
        h: 60 + Math.floor(Math.random() * 40),
        w: 140 + Math.floor(Math.random() * 40),
        hue: [175, 30, 340, 220, 45, 12][i % 6],
      })),
    [],
  );
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-center justify-center pb-40">
        {/* Phone frame */}
        <div className="relative w-[52%] max-w-[420px] aspect-[9/19] rounded-[42px] border-[3px] border-white/15 bg-black overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,200,180,0.35)]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black z-10" />
          {/* Scroll blur band top / bottom */}
          <div className="absolute inset-x-0 top-0 h-24 z-[5] bg-gradient-to-b from-black to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-24 z-[5] bg-gradient-to-t from-black to-transparent" />
          {/* Screenshots-folder header — sets the "this is your camera roll"
              context so viewers instantly parse the scrolling chips as
              screenshots, not just abstract shapes. */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="absolute top-10 inset-x-0 z-20 px-4 flex items-baseline justify-between"
          >
            <div className="font-display text-sm font-semibold text-white/90">
              Screenshots
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--primary))]">
              <CountingText from={0} to={1847} duration={2.5} /> items
            </div>
          </motion.div>
          {/* Scrolling chips — pure gradient placeholders EXCEPT the
              "Real Love" hero snap which threads through this scene too.
              We plant the real screenshot at chip index 4 (in the first
              copy) so it sails past mid-scroll — the viewer's eye picks
              up ONE identifiable music screenshot in the sea of chaos,
              seeding the continuity thread that pays off in Scenes 3, 4
              and 9. */}
          <motion.div
            className="absolute inset-x-4 top-20 flex flex-col gap-3 items-center"
            initial={{ y: 0 }}
            animate={{ y: -900 }}
            transition={{ duration: 5.5, ease: 'linear' }}
            style={{ filter: 'blur(0.5px)' }}
          >
            {chips.concat(chips).map((c, i) => {
              // Show the real "Real Love" snap on the first pass's chip #4
              // AND its duplicate on the second pass's chip #4 (indices 4
              // and 4+24 = 28) so it scrolls past twice for reinforcement.
              const isRealSnap = i === 4 || i === 28;
              return (
                <div
                  key={`chip-${c.id}-${i}`}
                  className="rounded-lg shadow-md overflow-hidden"
                  style={{
                    width: `${c.w}px`,
                    height: `${c.h}px`,
                    background: isRealSnap
                      ? '#000'
                      : `linear-gradient(135deg, hsl(${c.hue} 65% 45%), hsl(${c.hue} 55% 25%))`,
                  }}
                >
                  {isRealSnap && (
                    <img
                      src={DEMO_TRACK.rawSnapSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 2 — "Two thousand screenshots... eating up your phone's memory."
// Adds a horrifying memory meter climbing to 98%.
// -----------------------------------------------------------------------------
function BuriedScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-6 pb-60 px-8">
        <div className="w-full max-w-[440px]">
          {/* Static screenshot grid (paused chaos) — 3x3 keeps enough visual
              chaos without pushing the storage bar behind the caption strip.
              Reduced bottom margin (mb-8 → mb-5) so the storage meter sits
              ~12px higher and reads cleanly above the caption bar. */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {Array.from({ length: 9 }).map((_, i) => {
              const hue = [175, 30, 340, 220, 45, 12][i % 6];
              // Upper-left tile shows the real Nervous Records / "Real Love"
              // Instagram snap — same asset threaded through Scenes 3 and 9
              // so the viewer sees one specific screenshot travel through
              // the whole GroovLens lifecycle (buried → uploaded → scanned).
              const isFeaturedSnap = i === 0;
              return (
                <motion.div
                  key={`buried-${i}`}
                  initial={{ opacity: 0, scale: 0.85, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="aspect-[3/4] rounded-lg shadow-md overflow-hidden"
                  style={
                    isFeaturedSnap
                      ? undefined
                      : {
                          background: `linear-gradient(135deg, hsl(${hue} 65% 40%), hsl(${hue} 45% 20%))`,
                        }
                  }
                >
                  {isFeaturedSnap && (
                    <img
                      src={DEMO_TRACK.rawSnapSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
          {/* Phone memory meter */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="rounded-xl border border-white/15 bg-black/50 backdrop-blur px-4 py-3"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase tracking-widest text-white/60">
                iPhone Storage
              </span>
              <motion.span
                className="text-xs font-mono text-[#FFB24A]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3, duration: 0.4 }}
              >
                98% FULL
              </motion.span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                className="h-full"
                initial={{ width: '20%' }}
                animate={{ width: '98%' }}
                transition={{ delay: 1.6, duration: 2.4, ease: 'easeInOut' }}
                style={{
                  background:
                    'linear-gradient(90deg, hsl(var(--primary)) 0%, #FFB24A 100%)',
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 3 — "GroovLens fixes that. Snap one, or drop two hundred at once."
// Screenshots cascade into a scanner drop zone and get processed.
// -----------------------------------------------------------------------------
function SnapDropScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-32 pb-52 px-8">
        <div className="relative w-full max-w-[420px]">
          {/* Drop zone card */}
          <div className="rounded-2xl border-2 border-dashed border-[hsl(var(--primary))]/60 bg-black/40 backdrop-blur p-8 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-[hsl(var(--primary))] mb-2" />
            <div className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--primary))]">
              Bulk Upload
            </div>
            <div className="font-display text-2xl font-semibold mt-1 text-white">
              Drop them all.
            </div>
            <motion.div
              className="mt-4 text-xs font-mono text-white/70 tabular-nums"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5, duration: 0.4 }}
            >
              <CountingText from={0} to={214} duration={2.5} /> screenshots &middot; queued
            </motion.div>
          </div>

          {/* Snap & Seek callout — floats over the top-right of the drop
              zone to visually anchor the "snap a vinyl sleeve" narration.
              Teal accent matches the real Snap & Seek button color in the
              app; larger footprint here so it reads as a peer feature to
              Bulk Upload, not an afterthought. A soft ping pulse draws
              the eye to the new-feature affordance. */}
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 3.2, duration: 0.5, type: 'spring' }}
            className="absolute -top-4 -right-2 sm:right-0 flex items-center gap-2.5 rounded-2xl border-2 border-[hsl(var(--primary))] bg-black/85 backdrop-blur px-3.5 py-2 shadow-[0_10px_40px_-8px_hsl(var(--primary))] z-20"
            data-testid="promo-scene-snap-seek"
          >
            <div className="relative w-9 h-9 rounded-xl bg-[hsl(var(--primary))]/15 grid place-items-center shrink-0">
              <Camera className="w-5 h-5 text-[hsl(var(--primary))]" />
              {/* Ping pulse — subtle attention prompt for the new feature */}
              <span className="absolute inset-0 rounded-xl border-2 border-[hsl(var(--primary))]/60 animate-ping" />
            </div>
            <div className="leading-tight text-left">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--primary))]">
                Snap &amp; Seek
              </div>
              <div className="text-[11px] font-semibold text-white/95">
                Vinyl &middot; CD &middot; anywhere
              </div>
            </div>
          </motion.div>

          {/* Cascading screenshots — 10 gradient placeholders fall in
              and collapse into the drop zone. One "hero" tile carries
              the real Nervous Records "Real Love" snap and gets special
              treatment: bigger, arrives last, doesn't shrink as much,
              and sits above the others via z-index so it stays visible
              through the whole beat. Continuity thread with Scenes 2 & 9. */}
          {Array.from({ length: 10 }).map((_, i) => {
            const hue = [175, 30, 340, 220, 45, 12][i % 6];
            const startX = (i - 5) * 22;
            return (
              <motion.div
                key={`drop-${i}`}
                className="absolute rounded-lg shadow-lg overflow-hidden"
                initial={{
                  opacity: 0,
                  x: startX * 4,
                  y: -260,
                  rotate: startX,
                  scale: 0.9,
                }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: [startX * 4, startX * 2, 0, 0],
                  y: [-260, -80, 60, 60],
                  rotate: [startX, startX * 0.5, 0, 0],
                  scale: [0.9, 1, 0.7, 0.5],
                }}
                transition={{
                  delay: 0.4 + i * 0.15,
                  duration: 1.6,
                  times: [0, 0.4, 0.85, 1],
                  ease: 'easeInOut',
                }}
                style={{
                  width: 80,
                  height: 106,
                  left: '50%',
                  top: '50%',
                  marginLeft: -40,
                  marginTop: -53,
                  background: `linear-gradient(135deg, hsl(${hue} 65% 45%), hsl(${hue} 45% 25%))`,
                }}
              />
            );
          })}
          {/* Hero snap — arrives LAST (after the gradient cards start
              piling in) and cascades DOWN into the drop-zone graphic
              just like the other cards, becoming the final "screenshot"
              to disappear into GroovLens. Smaller footprint (150x200)
              so it doesn't dominate, higher zIndex so it stays on top
              during its fall, and same "collapse-into-drop-zone"
              trajectory as the placeholders. */}
          <motion.div
            className="absolute rounded-xl overflow-hidden shadow-[0_20px_50px_-10px_rgba(0,220,200,0.65)] ring-2 ring-[hsl(var(--primary))]/85"
            initial={{ opacity: 0, x: 30, y: -280, rotate: 8, scale: 0.9 }}
            animate={{
              opacity: [0, 1, 1, 1, 0],
              x: [30, 10, 0, 0, 0],
              y: [-280, -80, 40, 40, 60],
              rotate: [8, 4, 0, 0, 0],
              scale: [0.9, 1.05, 1, 1, 0.4],
            }}
            transition={{
              delay: 2.4,
              duration: 1.7,
              times: [0, 0.28, 0.5, 0.65, 1],
              ease: 'easeInOut',
            }}
            style={{
              width: 150,
              height: 200,
              left: '50%',
              top: '50%',
              marginLeft: -75,
              marginTop: -100,
              zIndex: 60,
            }}
            data-testid="promo-snapdrop-hero"
          >
            <img
              src={DEMO_TRACK.rawSnapSrc}
              alt=""
              className="w-full h-full object-cover"
            />
            {/* Little "Snap" label so viewers instantly parse it as an
                actual music screenshot, not a random image. */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent px-2 py-1">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[hsl(var(--primary))] leading-tight">
                Snap &middot; Real Love
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 4 — "AI reads every pixel."
// Features the REAL track card screenshot (with peek-a-boo deck navigation)
// front and center. A scanner line sweeps across to visualize the AI-read
// moment, and 3 key metadata badges (BPM / Key / Genre) pop in over the
// bottom edge as they get identified. The full artist/title/album already
// live inside the screenshot so we don't duplicate them.
// -----------------------------------------------------------------------------
function AIReadsScene() {
  const badges = [
    { label: 'BPM',   value: String(DEMO_TRACK.bpm),  icon: Waves,     delay: 0.6 },
    { label: 'Key',   value: DEMO_TRACK.key,          icon: Music4,    delay: 1.1 },
    { label: 'Genre', value: DEMO_TRACK.genre,        icon: Sparkles,  delay: 1.6 },
  ];
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-14 pb-52 px-4">
        <div className="w-full max-w-[420px] flex flex-col items-center">
          {/* Snap-to-card transformation sequence (updated for clarity):
              1. Raw snap enters BIG and centered — held on-screen long
                 enough for viewers to register the "screenshot" moment
              2. Teal scanner sweep runs top→bottom across the snap
              3. Snap gently slides + shrinks off to the left, dimmed
              4. Polished GroovLens track card flies in from the right and
                 lands centered/big — NO aggressive scale/crop so the full
                 modal is visible without any edges getting chopped off
              Same "Real Love" snap that appears in Scenes 2, 3, and 9. */}
          <div className="relative w-full h-[440px] sm:h-[480px]">
            {/* Raw snap — enters big, holds, then slides left & shrinks */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{
                opacity: [0, 1, 1, 0.55, 0.35],
                scale: [0.85, 1, 1, 0.55, 0.5],
                x: [0, 0, 0, -110, -120],
                y: [0, 0, 0, -10, -10],
                rotate: [0, 0, 0, -8, -8],
              }}
              transition={{
                duration: 5,
                times: [0, 0.12, 0.6, 0.82, 1],
                ease: 'easeInOut',
              }}
              className="absolute inset-x-0 mx-auto top-0 w-[68%] aspect-[9/16] rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10 z-10"
              data-testid="promo-airead-snap"
            >
              <img
                src={DEMO_TRACK.rawSnapSrc}
                alt=""
                className="w-full h-full object-cover"
              />
              {/* AI scan line — sweeps DOWN then back UP in orange (the
                  brand accent color used for scan/identification moments),
                  then fades as the snap starts shrinking left. Full round
                  trip visually communicates "AI reading every pixel".
                  Animating `top` (not `y`) because `%` on a transform
                  refers to the ELEMENT's own height (3px), not the parent
                  snap's — using `top` makes the sweep travel the full
                  height of the screenshot. */}
              <motion.div
                initial={{ opacity: 0, top: '-2%' }}
                animate={{
                  opacity: [0, 1, 1, 1, 0],
                  top: ['-2%', '100%', '100%', '-2%', '-2%'],
                }}
                transition={{
                  delay: 0.6,
                  duration: 2.2,
                  times: [0, 0.4, 0.5, 0.9, 1],
                  ease: 'linear',
                }}
                className="absolute left-0 right-0 h-[3px] bg-[#FFB24A] shadow-[0_0_18px_4px_#FFB24A] pointer-events-none"
              />
            </motion.div>

            {/* Polished GroovLens track card — flies in from the right after
                the scan completes and lands centered/BIG. Uses object-contain
                on a near-square container so the entire modal (with its neon
                gradient border) shows without any crop. Source is the new
                clean track-card image with no peek-a-boo deck edges. Centered
                via inset-x-0 + mx-auto so framer-motion's transforms don't
                fight with a tailwind -translate-x-1/2. */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5, x: 80, rotate: 8 }}
              animate={{
                opacity: [0, 0, 1, 1],
                scale: [0.5, 0.5, 1, 1],
                x: [80, 80, 0, 0],
                rotate: [8, 8, 0, 0],
              }}
              transition={{
                duration: 5,
                times: [0, 0.62, 0.82, 1],
                ease: 'easeOut',
              }}
              className="absolute inset-x-0 mx-auto top-2 w-[82%] aspect-[652/700] rounded-2xl overflow-hidden shadow-[0_30px_80px_-10px_rgba(0,220,200,0.55)] z-20"
              data-testid="promo-airead-card"
            >
              <img
                src={DEMO_TRACK.trackCardSrc}
                alt={`Identified track card: ${DEMO_TRACK.artistsFull} \u2014 ${DEMO_TRACK.title}`}
                className="w-full h-full object-contain block"
              />
            </motion.div>
          </div>

          {/* Metadata badges pop in after the transformation completes. */}
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {badges.map((b) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: b.delay + 2.0, duration: 0.35 }}
                className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/50 bg-black/70 backdrop-blur px-2.5 py-1"
              >
                <b.icon className="w-3 h-3 text-[hsl(var(--primary))]" />
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/60">
                  {b.label}
                </span>
                <span className="text-[11px] font-semibold text-white tabular-nums">
                  {b.value}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// Extra imagery paths from /public/promo — real screenshots that get
// dropped into scenes so the promo looks like an actual product tour.
const REAL_HOUSE_CRATE_SRC = '/promo/house-crate.jpg';
const REAL_STATS_PAGE_SRC = '/promo/stats-page.jpg';

// -----------------------------------------------------------------------------
// SCENE 5 — "Every track lands in the right crate. 33 genres, each with an
//           AI-painted cover — or design your own."
// The House tile at position [0] gets replaced with the REAL 4-way mosaic
// crate cover from the user's actual collection, so the grid shows one
// authentic tile side-by-side with the AI-painted gradient placeholders.
// -----------------------------------------------------------------------------
function CratesScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-14 pb-52 px-6">
        <div className="w-full max-w-[440px]">
          <div className="grid grid-cols-3 gap-3">
            {GENRE_TILES.map((t, i) => {
              const isRealHouseTile = i === 0; // real screenshot for House
              return (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, scale: 0.85, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                  className={`relative aspect-square rounded-xl overflow-hidden shadow-lg ${
                    isRealHouseTile ? 'ring-2 ring-[hsl(var(--primary))]/70' : ''
                  }`}
                  style={
                    isRealHouseTile
                      ? undefined
                      : {
                          background: `radial-gradient(circle at 30% 20%, hsl(${t.hue} ${t.sat}% 55%) 0%, hsl(${t.hue} ${t.sat}% 25%) 60%, hsl(${t.hue} ${t.sat}% 12%) 100%)`,
                        }
                  }
                >
                  {isRealHouseTile ? (
                    <img
                      src={REAL_HOUSE_CRATE_SRC}
                      alt="House crate — 14 pulls"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 p-2 flex flex-col justify-between">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/70">
                        Crate
                      </div>
                      <div>
                        <div className="font-display text-sm font-semibold text-white leading-tight drop-shadow">
                          {t.name}
                        </div>
                        <div className="text-[10px] font-mono text-white/60">
                          {6 + i * 3} tracks
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
          {/* Custom AI cover generator pill */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="mt-4 rounded-xl border border-[#FFB24A]/40 bg-black/60 backdrop-blur px-3 py-2.5 flex items-center gap-2.5"
          >
            <div className="w-9 h-9 rounded-lg bg-[#FFB24A]/15 grid place-items-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#FFB24A]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFB24A]">
                Custom AI cover generator
              </div>
              <div className="text-xs text-white/80 leading-tight mt-0.5 truncate">
                &ldquo;sunset ibiza rooftop, warm gradient, film grain&rdquo;
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.4 }}
            className="mt-3 text-center text-xs font-mono uppercase tracking-widest text-[hsl(var(--primary))]"
          >
            33 sharp genre crates &middot; unique AI covers
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 6 — "Previews. Buy links... Music videos and ringtones... Parse a
//           whole tracklist at once."
// Second-cut update:
//   - Juno REMOVED (store winding down as of early 2026).
//   - Traxsource added (was already in-app).
//   - New "Video + Ringtones" tile (Apple Music + YouTube).
//   - Wording changed from "festival lineup" -> "tracklist" (broader appeal).
// -----------------------------------------------------------------------------
function ToolkitScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-24 pb-52 px-6">
        <div className="w-full max-w-[440px] space-y-2.5">
          {/* Preview waveform */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-black/60 backdrop-blur p-3 flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-md bg-[hsl(var(--primary))] grid place-items-center shrink-0">
              <Play className="w-4 h-4 text-black fill-black" />
            </div>
            <div className="flex-1 flex items-end gap-[3px] h-7">
              {Array.from({ length: 32 }).map((_, i) => (
                <motion.div
                  key={`wave-${i}`}
                  className="flex-1 bg-[hsl(var(--primary))] rounded-sm"
                  initial={{ height: '20%' }}
                  animate={{
                    height: [`${20 + (i * 7) % 60}%`, `${30 + (i * 13) % 55}%`, `${20 + (i * 11) % 60}%`],
                  }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.03 }}
                />
              ))}
            </div>
          </motion.div>

          {/* Buy links pills — Traxsource now included, Juno removed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-black/60 backdrop-blur p-3"
          >
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-2">
              Buy on
            </div>
            <div className="flex flex-wrap gap-1.5">
              {['Beatport', 'Traxsource', 'Bandcamp', 'iTunes', 'Amazon'].map((store, i) => (
                <motion.span
                  key={store}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.1 + i * 0.08 }}
                  className="inline-flex items-center gap-1 rounded-full border border-[#FFB24A]/50 bg-[#FFB24A]/10 px-2 py-1 text-[10px] font-mono text-[#FFB24A]"
                >
                  <ShoppingCart className="w-3 h-3" />
                  {store}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Music videos + ringtones — new feature callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-black/60 backdrop-blur p-3"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-red-500/15 grid place-items-center shrink-0">
                <Video className="w-4 h-4 text-red-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">
                  Watch + Ringtones
                </div>
                <div className="text-xs text-white/85 leading-tight mt-0.5">
                  Music videos on YouTube &middot; ringtones on Apple Music
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tracklist parser — wording updated */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.6, duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-black/60 backdrop-blur p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <ListMusic className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                Tracklist Parser
              </span>
              <span className="ml-auto text-[10px] font-mono text-[hsl(var(--primary))]">
                <CountingText from={0} to={28} duration={2} /> / 30
              </span>
            </div>
            <div className="space-y-1">
              {[
                'Kerri Chandler \u2014 Track 1',
                'Prospa \u2014 Ecstasy',
                'Fred again.. \u2014 Marea',
                'Peggy Gou \u2014 Starry Night',
              ].map((row, i) => (
                <motion.div
                  key={row}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 2.8 + i * 0.25 }}
                  className="flex items-center gap-2 text-[11px] text-white/90"
                >
                  <Star className="w-3 h-3 text-[#FFB24A] fill-[#FFB24A] shrink-0" />
                  <span className="truncate">{row}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 7 — "Discover similar tracks by artist, vibe, or BPM. Dive deep into
//           any artist's bio, top tracks, and related sound."
// Features the REAL Marco Lys artist deep-dive screenshot, plus a small
// floating "Real Love" track thumbnail in the corner that ties this scene
// back to Scene 4 — the video reads as: user scanned Real Love, now diving
// into the artist behind it.
// -----------------------------------------------------------------------------
function DiscoverScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-12 pb-52 px-4">
        <div className="relative w-full max-w-[420px]">
          {/* Real artist deep dive screenshot — the "dive deep into any
              artist's bio" half of the narration. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-[0_25px_80px_-15px_rgba(0,200,180,0.4)]"
          >
            <img
              src={DEMO_TRACK.artistDeepDiveSrc}
              alt={`${DEMO_TRACK.artist} artist deep dive page`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 rounded-2xl ring-1 ring-[hsl(var(--primary))]/25 pointer-events-none" />
          </motion.div>

          {/* Track-card thumb — ties back to Scene 4. Uses the same clean
              track-card image (no peek-a-boo) as Scene 4, sized as a small
              square corner thumb. object-cover with center position works
              fine here since the source is already just the modal. */}
          <motion.div
            initial={{ opacity: 0, x: 30, y: -20, rotate: 8 }}
            animate={{ opacity: 1, x: 0, y: 0, rotate: 6 }}
            transition={{ delay: 1.2, duration: 0.5, type: 'spring' }}
            className="absolute -top-3 right-2 sm:right-3 w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden shadow-[0_10px_30px_-4px_rgba(0,0,0,0.7)] ring-2 ring-[#FFB24A]/60 z-10"
            data-testid="promo-scene-track-link"
          >
            <img
              src={DEMO_TRACK.trackCardSrc}
              alt={DEMO_TRACK.title}
              className="w-full h-full object-contain bg-black block"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
              <div className="text-[8px] font-mono uppercase tracking-widest text-[#FFB24A] leading-tight truncate">
                Scanned
              </div>
              <div className="text-[9px] font-semibold text-white leading-tight truncate">
                {DEMO_TRACK.title}
              </div>
            </div>
          </motion.div>

          {/* Real Discover Similar modal screenshot — ENLARGED and
              re-centered under the artist deep dive so the modal title
              is clearly readable. Sits mostly BELOW the frame, peeking
              well up over the bottom edge as an overlay hint. */}
          <motion.div
            initial={{ opacity: 0, x: 20, y: 40, rotate: 4, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, y: 0, rotate: 2, scale: 1 }}
            transition={{ delay: 2.0, duration: 0.7, type: 'spring', bounce: 0.25 }}
            className="absolute -bottom-32 inset-x-0 mx-auto w-[92%] max-w-[380px] rounded-xl overflow-hidden shadow-[0_25px_60px_-10px_rgba(0,220,200,0.5)]"
            data-testid="promo-scene-discover-similar"
          >
            <img
              src={DEMO_TRACK.discoverSimilarSrc}
              alt="Discover similar tracks modal"
              className="w-full h-auto block"
            />
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 8 — "Flag what you own. Star what you want. Watch your stats grow.
//            Share crates privately, or push to Spotify."
// Uses the REAL personal-stats screenshot as the hero visual (cropped to the
// top ~55% via object-position so the 4 stat cards + genre-breakdown donut
// dominate the frame). Own/Want counters sit above, Share/Spotify below.
// -----------------------------------------------------------------------------
function OrganizeScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-16 pb-52 px-6">
        <div className="w-full max-w-[440px] space-y-2.5">
          {/* Own + Want flags row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-black/60 backdrop-blur px-3 py-2 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#FFB24A]/15 grid place-items-center">
                <Star className="w-3.5 h-3.5 text-[#FFB24A] fill-[#FFB24A]" />
              </div>
              <div className="leading-tight">
                <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFB24A]">Want list</div>
                <div className="text-[11px] text-white/80"><CountingText from={0} to={42} duration={1.2} /> tracks</div>
              </div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[hsl(var(--primary))]/15 grid place-items-center">
                <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              </div>
              <div className="leading-tight">
                <div className="text-[9px] font-mono uppercase tracking-widest text-[hsl(var(--primary))]">Owned</div>
                <div className="text-[11px] text-white/80"><CountingText from={0} to={118} duration={1.4} /> tracks</div>
              </div>
            </div>
          </motion.div>

          {/* Real stats page screenshot — full unclipped view. Source
              image is nearly square (892×867), so an aspect-square
              container + object-contain shows the WHOLE stats page
              (4 stat cards, genre donut, and detail rows below). */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="relative rounded-xl overflow-hidden shadow-lg aspect-square ring-1 ring-white/10 bg-black"
          >
            <img
              src={REAL_STATS_PAGE_SRC}
              alt="Your GroovLens stats: 42 pulls, 6 genres, donut chart"
              className="absolute inset-0 w-full h-full object-contain"
            />
          </motion.div>

          {/* Share + Spotify export row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9, duration: 0.4 }}
            className="flex items-center gap-2"
          >
            <div className="flex-1 rounded-xl border border-white/10 bg-black/60 backdrop-blur p-2.5 flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              <span className="text-[10px] text-white/80">Share crate privately</span>
            </div>
            <div className="flex-1 rounded-xl bg-[#1DB954] p-2.5 flex items-center gap-2">
              <Music4 className="w-3.5 h-3.5 text-black" />
              <span className="text-[10px] font-semibold text-black">Export to Spotify</span>
            </div>
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 8 — "That song you saved and forgot about? Still there."
// Quiet, emotional moment. Single spotlit card with the branded logo mark
// centered on top and the raw camera-roll snap ghosted behind it — same
// specific snap the viewer has watched travel through Scenes 2 and 3, now
// preserved inside the GroovLens vault. Card layers (bottom → top):
//   1. Raw Instagram snap as background image
//   2. Semi-transparent teal→dark gradient overlay so the snap reads as
//      "ghosted" (visible tint, not the crisp original)
//   3. Logo mark + "Still findable · Never lost" copy
// -----------------------------------------------------------------------------
function NeverLostScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-24 pb-52 px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-[300px]"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-[0_25px_80px_-10px_rgba(255,178,74,0.35)]">
            {/* Layer 1 — ghosted raw snap fills the whole card. */}
            <img
              src={DEMO_TRACK.rawSnapSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              aria-hidden="true"
            />
            {/* Layer 2 — semi-transparent teal→dark gradient overlay
                keeps the brand tone dominant while still letting the
                snap peek through (that "still there, still findable"
                emotional resonance). */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, hsl(175 65% 40% / 0.78), hsl(220 45% 20% / 0.82))',
              }}
            />
            {/* Layer 3 — foreground content (logo + text) sits on top. */}
            <div className="relative">
              <div className="aspect-square flex items-center justify-center">
                <img
                  src="/brand/groovlens-icon-transparent-512.png"
                  alt=""
                  className="w-24 h-24 opacity-95 drop-shadow-[0_0_20px_rgba(0,0,0,0.55)]"
                />
              </div>
              <div className="p-4 bg-black/30 backdrop-blur-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                  Scanned &middot; saved &middot; safe
                </div>
                <div className="font-display text-lg font-semibold text-white mt-1">
                  Never lost. Never forgotten.
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 10 (NEW) — "Install GroovLens right from the site. No app store."
// Phone shows the PWA install prompt / home-screen icon appearing.
// -----------------------------------------------------------------------------
function InstallScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex items-start justify-center pt-14 pb-52 px-8">
        <div className="w-full max-w-[400px] flex flex-col items-center">
          {/* Phone home screen with GroovLens icon fading in */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative w-52 aspect-[9/19] rounded-[36px] border-[3px] border-white/15 bg-gradient-to-b from-[#1a2340] via-[#0f1830] to-[#0a1024] overflow-hidden shadow-[0_30px_80px_-10px_rgba(0,200,180,0.35)]"
          >
            {/* Notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-black z-10" />
            {/* App icon grid */}
            <div className="pt-10 px-4 grid grid-cols-4 gap-3">
              {Array.from({ length: 11 }).map((_, i) => (
                <div
                  key={`app-${i}`}
                  className="aspect-square rounded-lg bg-white/10"
                />
              ))}
              {/* The GroovLens PWA install icon — pops in with a shimmer.
                  Uses the EXACT icon-192.png from the manifest so what
                  viewers see in the promo is the identical teal app-square
                  tile they'll get on their home screen after install. */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5, rotate: -8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 1.5, duration: 0.55, type: 'spring' }}
                className="aspect-square rounded-lg overflow-hidden shadow-[0_10px_24px_-4px_rgba(0,0,0,0.8)]"
              >
                <img
                  src="/icons/icon-192.png"
                  alt="GroovLens app icon"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
            {/* Home indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-white/40" />
          </motion.div>

          {/* PWA install callout */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.4, duration: 0.4 }}
            className="mt-4 rounded-full bg-black/60 backdrop-blur border border-[hsl(var(--primary))]/40 px-4 py-2 flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
            <span className="text-xs font-mono text-white/90">
              Install from the site &middot; no app store
            </span>
          </motion.div>
        </div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// SCENE 11 (was 9) — CTA. Big wordmark, tagline, URL.
// -----------------------------------------------------------------------------
function CTAScene() {
  return (
    <SceneBackdrop>
      <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-display text-6xl sm:text-7xl font-black tracking-tight text-white"
        >
          Groov<span className="text-[hsl(var(--primary))]">Lens</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="h-[3px] w-32 mt-3 rounded-full"
          style={{
            background:
              'linear-gradient(90deg, hsl(var(--primary)) 0%, #FFB24A 100%)',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="mt-6 text-2xl font-display font-medium text-white/90"
        >
          Groovlens.com
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.0, duration: 0.5 }}
          className="mt-6"
        >
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-black font-semibold px-6 py-3 hover:scale-105 transition-transform"
            data-testid="promo-cta-signup"
          >
            <Sparkles className="w-4 h-4" />
            Go dig
          </Link>
        </motion.div>
        {/* QR code — the ONE thing that turns a passive video into a live
            conversion channel. Viewers scanning from TikTok/Reels/YouTube
            on their phone land straight in the 7-day free trial funnel.
            Rendered as SVG so it's crisp at any recording resolution and
            adds zero PNG asset overhead. */}
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 2.8, duration: 0.5, type: 'spring' }}
          className="mt-6 flex flex-col items-center gap-1.5"
          data-testid="promo-cta-qr"
        >
          <div className="p-2 rounded-xl bg-white shadow-[0_0_28px_-8px_hsl(var(--primary))]">
            <QRCodeSVG
              value="https://groovlens.com/signup"
              size={92}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#0B0F14"
              marginSize={0}
            />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
            Scan to start
          </div>
        </motion.div>
      </div>
    </SceneBackdrop>
  );
}

// -----------------------------------------------------------------------------
// Small animated integer counter used inside a couple of scenes.
// -----------------------------------------------------------------------------
function CountingText({ from = 0, to = 100, duration = 1.5 }) {
  const [n, setN] = useState(from);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000));
      setN(Math.round(from + (to - from) * p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span className="tabular-nums">{n}</span>;
}

// -----------------------------------------------------------------------------
// Scene registry — used by the switcher.
// -----------------------------------------------------------------------------
const SCENE_COMPONENTS = {
  cover: CoverScene,
  folder: FolderScene,
  buried: BuriedScene,
  snapdrop: SnapDropScene,
  airead: AIReadsScene,
  crates: CratesScene,
  toolkit: ToolkitScene,
  discover: DiscoverScene,
  organize: OrganizeScene,
  neverlost: NeverLostScene,
  install: InstallScene,
  cta: CTAScene,
};

// -----------------------------------------------------------------------------
// Main page.
// -----------------------------------------------------------------------------
export default function Promo() {
  // Read ?record=1 or ?record=true from URL to auto-hide chrome and autoplay.
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const recordParam = params.get('record');
  const [recordingMode, setRecordingMode] = useState(recordParam === '1' || recordParam === 'true');
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [playing, setPlaying] = useState(true); // autoplay on mount
  const [elapsed, setElapsed] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const pauseAccumRef = useRef(0);
  const pausedAtRef = useRef(null);

  // Advance the clock via requestAnimationFrame while playing.
  useEffect(() => {
    if (!playing) {
      pausedAtRef.current = performance.now();
      cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    // Resuming — subtract the paused interval so `elapsed` stays continuous.
    if (pausedAtRef.current !== null) {
      pauseAccumRef.current += performance.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    } else {
      startRef.current = performance.now();
    }
    const tick = (now) => {
      const secs = (now - startRef.current - pauseAccumRef.current) / 1000;
      if (secs >= TOTAL_SEC) {
        setElapsed(TOTAL_SEC);
        setPlaying(false);
        return;
      }
      setElapsed(secs);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  const restart = () => {
    cancelAnimationFrame(rafRef.current);
    startRef.current = performance.now();
    pauseAccumRef.current = 0;
    pausedAtRef.current = null;
    setElapsed(0);
    setPlaying(true);
  };

  // Seek to a specific second on the timeline. Rewrites the clock origin so
  // the RAF loop keeps ticking correctly from wherever the scrubber landed.
  // The user can drag the slider or click a scene chapter marker to jump
  // directly to any moment — no more re-watching the whole thing to preview
  // a single scene.
  const seek = (seconds) => {
    const clamped = Math.max(0, Math.min(TOTAL_SEC, seconds));
    // Rebase the start-time reference so `elapsed` computes to `clamped`
    // whether we're currently playing or paused.
    startRef.current = performance.now() - clamped * 1000;
    pauseAccumRef.current = 0;
    if (pausedAtRef.current !== null) {
      // If we're paused, keep the "just seeked" position fresh so a future
      // resume doesn't add back the paused interval.
      pausedAtRef.current = performance.now();
    }
    setElapsed(clamped);
  };

  // Format seconds → "M:SS" for the time-display readout.
  const fmtTime = (s) => {
    const total = Math.max(0, Math.round(s));
    const m = Math.floor(total / 60);
    const sec = total % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const currentScene = SCENES.find((s) => elapsed >= s.startSec && elapsed < s.endSec) || SCENES[SCENES.length - 1];
  const SceneComponent = SCENE_COMPONENTS[currentScene.key];
  const progressPct = Math.min(100, (elapsed / TOTAL_SEC) * 100);

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden">
      {/* 9:16 stage — locked aspect ratio, centered on any viewport */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative bg-black overflow-hidden"
          style={{
            aspectRatio: '9 / 16',
            height: '100vh',
            maxWidth: '100vw',
          }}
          data-testid="promo-stage"
        >
          {/* Scene switcher — Framer AnimatePresence crossfades */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentScene.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <SceneComponent />
            </motion.div>
          </AnimatePresence>

          {/* Progress bar (top edge) — hidden in recording mode */}
          {!recordingMode && (
            <div className="absolute top-0 inset-x-0 h-1 bg-white/10 z-30">
              <div
                className="h-full transition-[width] duration-100"
                style={{
                  width: `${progressPct}%`,
                  background:
                    'linear-gradient(90deg, hsl(var(--primary)) 0%, #FFB24A 100%)',
                }}
              />
            </div>
          )}

          {/* Subtitle overlay — sits in the reserved bottom band so it never
              overlaps scene content (scenes use pb-52 to leave this space). */}
          {showSubtitles && (
            <div className="absolute bottom-20 inset-x-0 px-6 z-20 pointer-events-none">
              <div
                className="mx-auto max-w-[90%] text-center rounded-lg bg-black/70 backdrop-blur px-3 py-1.5 text-xs sm:text-sm leading-snug text-white shadow"
                data-testid="promo-subtitle"
              >
                {currentScene.line}
              </div>
            </div>
          )}

          {/* Controls — hidden in recording mode so screen-capture is clean.
              Timeline scrubber removed from the visible chrome so nothing
              overlaps the promo stage while the user sets up their screen
              recorder. Play / Pause / Restart / CC / Record buttons remain
              so the user can still control playback pre-recording. */}
          {!recordingMode && (
            <div className="absolute bottom-4 inset-x-0 z-30 px-4 flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className="w-11 h-11 rounded-full bg-white text-black grid place-items-center shadow hover:scale-105 transition-transform"
                aria-label={playing ? 'Pause' : 'Play'}
                data-testid="promo-play-toggle"
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button
                type="button"
                onClick={restart}
                className="h-9 rounded-full bg-white/10 backdrop-blur px-3 flex items-center gap-1.5 text-xs font-medium hover:bg-white/15 transition-colors"
                aria-label="Restart"
                data-testid="promo-restart"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Restart
              </button>
              <button
                type="button"
                onClick={() => setShowSubtitles((s) => !s)}
                className={`h-9 rounded-full px-3 flex items-center gap-1.5 text-xs font-medium backdrop-blur transition-colors ${
                  showSubtitles ? 'bg-[hsl(var(--primary))] text-black' : 'bg-white/10 hover:bg-white/15'
                }`}
                aria-label="Toggle subtitles"
                data-testid="promo-subtitles-toggle"
              >
                <Subtitles className="w-3.5 h-3.5" />
                CC
              </button>
              <button
                type="button"
                onClick={() => setRecordingMode(true)}
                className="h-9 rounded-full bg-[#FFB24A] text-black px-3 flex items-center gap-1.5 text-xs font-semibold hover:bg-[#FFC46E] transition-colors"
                aria-label="Recording mode"
                data-testid="promo-record-mode"
                title="Hide all UI and start recording your screen"
              >
                <Video className="w-3.5 h-3.5" />
                Record
              </button>
              </div>
            </div>
          )}

          {/* Recording mode escape hatch (top-right, subtle) */}
          {recordingMode && (
            <button
              type="button"
              onClick={() => setRecordingMode(false)}
              className="absolute top-3 right-3 z-40 w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 grid place-items-center backdrop-blur transition-colors opacity-30 hover:opacity-100"
              aria-label="Exit recording mode"
              data-testid="promo-record-mode-exit"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Recording tips — off-stage, only shown when NOT recording */}
      {!recordingMode && (
        <div className="absolute top-3 left-3 z-30 max-w-[240px] text-[10px] font-mono leading-relaxed text-white/50 pointer-events-none">
          <div className="text-white/80 text-[11px] font-semibold mb-1">
            How to screen-record this
          </div>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Turn off subtitles if you&rsquo;ll dub your own VO.</li>
            <li>Hit &ldquo;Record&rdquo; to hide the controls.</li>
            <li>Start your OS screen recorder.</li>
            <li>Hit Restart. Wait 60s. Stop.</li>
            <li>Drop the video into CapCut, add VO + music.</li>
          </ol>
        </div>
      )}

      {!recordingMode && (
        <Link
          to="/"
          className="absolute top-3 right-3 z-30 text-[10px] font-mono text-white/50 hover:text-white/90 pointer-events-auto"
          data-testid="promo-exit-link"
        >
          &larr; back to site
        </Link>
      )}
    </div>
  );
}
